import lunr from 'lunr';
import { ngram } from 'ngramable';
import pako from 'pako';

/* eslint-disable no-console */
class LunrSearch {
  constructor() {
    let opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.idx = null;
    this.docs = new Map();
    this.refId = opts.ref || 'id';
    this.dataKey = opts.dataKey || '_originalData';
    this.debug = opts.debug || false;
    this.useStemming = opts.useStemming !== false;
    this.useStopWords = opts.useStopWords !== false;
  }
  init() {
    let opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (this.debug) console.time('\u263D Index Built');
    const fields = opts.fields || [];
    this.refId = opts.ref || 'id';
    this.dataKey = opts.dataKey || '_originalData';
    this.docs = opts.docs || this.docs;
    const that = this;
    this.idx = lunr(function buildIdx() {
      if (!that.useStemming) {
        if (that.debug) console.log('\u263D Not using stemmer');
        this.pipeline.remove(lunr.stemmer);
        this.searchPipeline.remove(lunr.stemmer);
      }
      if (!that.useStopWords) {
        if (that.debug) console.log('\u263D Not using stop words');
        this.pipeline.remove(lunr.stopWordFilter);
      }
      this.ref(that.refId);
      fields.forEach(field => this.field(field));
      that.docs.forEach(function processDocs(doc) {
        this.add(doc);
      }, this);
    });
    if (this.debug) console.timeEnd('\u263D Index Built');
  }
  dataForSerialization() {
    return {
      idx: this.idx,
      docs: this.docs
    };
  }
  loadIndex(serializedIdx) {
    if (this.debug) console.time('\u263D unserializedIdx');
    let storedData = serializedIdx;
    if (typeof serializedIdx === 'string') {
      storedData = JSON.parse(serializedIdx);
    }
    this.idx = lunr.Index.load(storedData.idx);
    this.docs = new Map(storedData.docs);
    if (this.debug) console.timeEnd('\u263D unserializedIdx');
    return true;
  }
  search(searchPhrase) {
    if (this.debug) console.time(`\u263D searching on ${searchPhrase}`);
    if (!this.idx) {
      console.warn('A call was made to the LunrSearch.search method before the index was built.');
      return false;
    }
    const results = this.idx.search(searchPhrase).map(res => {
      return this.docs.get(res.ref)[this.dataKey];
    });
    if (this.debug) console.timeEnd(`\u263D searching on ${searchPhrase}`);
    return results;
  }
}

const SEARCH_MESSAGES = {
  BUILD_INDEX: 'buildIndex',
  SERIALIZE_INDEX: 'serializeIndex',
  LOAD_INDEX: 'loadIndex',
  QUERY: 'query',
  RELEVANCE_VALUE: '*relevance*'
};

var version="2.0.4";

/* eslint-disable no-console */
const {
  BUILD_INDEX,
  SERIALIZE_INDEX,
  LOAD_INDEX,
  QUERY
} = SEARCH_MESSAGES;

/**
 * Will check if byte string and will inflate if needed before parsing.
 * @param {string} inputStr An input string to be parsed. Can be a byte string or a char string.
 * @returns {Object}
 */
const inflateAndParse = inputStr => {
  return JSON.parse(inputStr[0] === 'x' ? pako.inflate(inputStr, {
    to: 'string'
  }) : inputStr);
};
const initSearchWorker = function () {
  let opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const defaultOpts = {
    generateDoc: null,
    // Function for generating a document
    refField: '_id',
    partialSearchFields: null,
    searchFields: null,
    useStemming: true,
    useStopWords: true,
    debug: false
  };
  const {
    generateDoc,
    searchFields,
    partialSearchFields,
    refField,
    debug,
    useStemming,
    useStopWords
  } = {
    ...defaultOpts,
    ...opts
  };
  const makeNGramKey = field => `${field}NGrams`;
  const nGramString = phrase => {
    // Use a set to prevent duplicates
    const nGrams = new Set();
    phrase.split(' ').forEach(word => {
      ngram(word, {
        style: 2,
        min: 2,
        max: 15
      }).forEach(partial => nGrams.add(partial));
    });
    return [...nGrams.values()].join(' ');
  };
  const applyNGrams = item => {
    const ngrams = Object.keys(item).reduce((acc, field) => {
      if (partialSearchFields === null || partialSearchFields.includes(field)) {
        acc[makeNGramKey(field)] = nGramString(item[field]);
      }
      return acc;
    }, {});
    return {
      ...item,
      ...ngrams
    };
  };
  const generateDocuments = data => {
    const docs = new Map();
    data.forEach(item => {
      const document = applyNGrams(generateDoc(item));
      docs.set(item[refField], {
        ...document,
        _originalData: item
      });
    });
    return docs;
  };
  const getKeysFromDoc = docsMap => Object.keys(docsMap.values().next().value).slice(0, -1);
  const getNGramKeys = docsMap => partialSearchFields === null ? getKeysFromDoc(docsMap).map(field => makeNGramKey(field)) : partialSearchFields.map(field => makeNGramKey(field));
  const searchEngine = new LunrSearch({
    debug,
    useStemming,
    useStopWords
  });
  let originalData;
  const indexData = data => {
    originalData = data;
    const docsMap = generateDocuments(data);
    // if not searchFields provided, use all except the last which will be _originalData
    const resolvedSearchFields = [...(searchFields === null ? getKeysFromDoc(docsMap) : searchFields), ...getNGramKeys(docsMap)];
    searchEngine.init({
      docs: docsMap,
      ref: refField,
      fields: resolvedSearchFields
    });
  };
  const serializeData = function () {
    let shouldCompress = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (debug) console.time('\u263D serializedSearch');
    const searchData = searchEngine.dataForSerialization();
    const output = {
      version: version,
      docs: [...searchData.docs],
      // Maps cannot be stringified so spread it into an array
      idx: searchData.idx,
      originalData
    };
    const serializedSearch = JSON.stringify(output);
    if (!shouldCompress) {
      if (debug) console.timeEnd('\u263D serializedSearch');
      return serializedSearch;
    }
    if (debug) console.timeEnd('\u263D serializedSearch');
    return pako.deflate(serializedSearch, {
      to: 'string'
    });
  };
  addEventListener('message', event => {
    const {
      task,
      payload
    } = event.data;
    if (!task) {
      console.warn('Task was sent that is undefined, null, or falsey value.');
      return;
    }
    switch (task) {
      case BUILD_INDEX:
        {
          if (payload) {
            indexData(payload);
            postMessage({
              task: BUILD_INDEX,
              payload: true
            });
          }
          break;
        }
      case SERIALIZE_INDEX:
        {
          const {
            shouldCompress = false
          } = payload;
          const serializedSearch = serializeData(shouldCompress);
          postMessage({
            task: SERIALIZE_INDEX,
            payload: serializedSearch
          });
          break;
        }
      case LOAD_INDEX:
        {
          const {
            serializedIdx
          } = payload;
          const result = searchEngine.loadIndex(serializedIdx);
          postMessage({
            task: LOAD_INDEX,
            payload: result
          });
          break;
        }
      case QUERY:
        {
          const results = searchEngine.search(payload) || [];
          postMessage({
            task: QUERY,
            payload: results
          });
          break;
        }
      default:
        console.error(`SearchWorker: No task defined for ${task}`);
        console.log('SearchWorker: Failed Event Data', event);
        break;
    }
  });
};

export { LunrSearch, SEARCH_MESSAGES, inflateAndParse, initSearchWorker };
//# sourceMappingURL=index.esm.js.map
